import Cookies from 'js-cookie';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { HitVariant, HitVariants } from '@components/algolia/Hit';
import { buildPrice } from '@hooks/useHitPrice';
import useCustomer from '@adapters/bigcommerce/hooks/customer/useCustomer';
import { getGroupKeyByCustomerGroupId } from '@utils/selectPriceByGroup';

// Hoisted for stable reference (js-cache-function-results style; used inside useMemo)
function isSamePriceRange(
  a: { price?: [number, number]; salePrice?: [number, number] } | undefined,
  b: { price?: [number, number]; salePrice?: [number, number] } | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const priceEqual =
    (!a.price && !b.price) ||
    (a.price != null &&
      b.price != null &&
      a.price[0] === b.price[0] &&
      a.price[1] === b.price[1]);
  const salePriceEqual =
    (!a.salePrice && !b.salePrice) ||
    (a.salePrice != null &&
      b.salePrice != null &&
      a.salePrice[0] === b.salePrice[0] &&
      a.salePrice[1] === b.salePrice[1]);
  return priceEqual && salePriceEqual;
}

type OverrideHitVariant = Omit<HitVariant, 'priceRange'>;

export type Swatch = OverrideHitVariant & {
  colourQuerySlug: string;
  swatchName: string;
  isActive: boolean;
  priceFormatted?: string;
  salePriceFormatted?: string;
  priceRange?: {
    price?: string;
    salePrice?: string;
  };
  guestPriceFormatted?: string;
  guestSalePriceFormatted?: string;
  guestPriceRange?: {
    price?: string;
    salePrice?: string;
  };
  hasMemberPriceDifference: boolean;
  shouldShowGuestPriceComparison: boolean;
  hasCustomerGroupPriceDifference: boolean;
  onClick: () => void;
};

export const useHitSwatches = (
  hitColour: string,
  variants: HitVariants,
  defaultState: Pick<
    HitVariant,
    | 'image'
    | 'imageTransparent'
    | 'badge'
    | 'badgeGuest'
    | 'price'
    | 'salePrice'
    | 'sku'
  >,
  locale?: string
) => {
  const [activeVariant, setActiveVariant] = useState(hitColour);
  const [hasMounted, setHasMounted] = useState(false);
  const algoliaDebug = Cookies.get('algoliaDebug');
  const { customer } = useCustomer();

  // Only apply customer pricing after mount to avoid hydration issues
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Get price group key based on customer group ID (only after mount)
  const priceGroupKey =
    hasMounted && customer?.customerGroupId
      ? getGroupKeyByCustomerGroupId(customer.customerGroupId)
      : 'guest';

  const setActiveVariantCallback = useCallback((variant: string) => {
    setActiveVariant(variant);
  }, []);

  const swatchesRecord = useMemo(() => {
    let result: Record<string, Swatch> = Object.keys(variants ?? {}).reduce(
      (dict, variant) => {
        const {
          swatch,
          image,
          imageTransparent,
          badge,
          badgeGuest,
          sku,
          price,
          salePrice,
          priceRange,
          prices,
        } = variants[variant];

        // Select price based on customer group if multi-group prices exist.
        // `salePrice` falls back to the legacy top-level field only when this
        // group has no entry at all in `prices` — a present entry missing
        // `salePrice` means that group legitimately has no sale (0), and must
        // not inherit another group's legacy sale price. `price`/`priceRange`
        // don't carry that ambiguity (a present group entry always has its
        // own price), so they keep the simple per-field fallback.
        const selectedPrice = prices?.[priceGroupKey]?.price ?? price;
        const selectedSalePrice = prices?.[priceGroupKey]
          ? prices[priceGroupKey]?.salePrice ?? 0
          : salePrice;
        const selectedPriceRange =
          prices?.[priceGroupKey]?.priceRange ?? priceRange;

        // Guest prices for comparison
        const guestPrice = prices?.guest?.price ?? price;
        const guestSalePrice = prices?.guest
          ? prices.guest.salePrice ?? 0
          : salePrice;
        const guestPriceRangeData = prices?.guest?.priceRange ?? priceRange;

        // Member prices for comparison (to determine if member pricing exists)
        const memberPrice = prices?.member?.price ?? price;
        const memberSalePrice = prices?.member
          ? prices.member.salePrice ?? 0
          : salePrice;
        const memberPriceRange = prices?.member?.priceRange ?? priceRange;

        const {
          priceFormatted,
          salePriceFormatted,
          normalPriceRange,
          salePriceRange,
        } = buildPrice(
          selectedPrice,
          selectedSalePrice,
          selectedPriceRange,
          locale
        );

        // By default, reuse selected formatted values for guest
        let guestPriceFormatted = priceFormatted;
        let guestSalePriceFormatted = salePriceFormatted;
        let guestNormalPriceRange = normalPriceRange;
        let guestSalePriceRange = salePriceRange;

        // Only compute separate guest-formatted fields when needed
        if (hasMounted && priceGroupKey !== 'guest') {
          const {
            priceFormatted: computedGuestPriceFormatted,
            salePriceFormatted: computedGuestSalePriceFormatted,
            normalPriceRange: computedGuestNormalPriceRange,
            salePriceRange: computedGuestSalePriceRange,
          } = buildPrice(
            guestPrice,
            guestSalePrice,
            guestPriceRangeData,
            locale
          );

          guestPriceFormatted = computedGuestPriceFormatted;
          guestSalePriceFormatted = computedGuestSalePriceFormatted;
          guestNormalPriceRange = computedGuestNormalPriceRange;
          guestSalePriceRange = computedGuestSalePriceRange;
        }

        // Determine if member price differs from guest price (regardless of current user's group)
        const hasMemberPriceDifference =
          hasMounted &&
          (memberPrice !== guestPrice ||
            memberSalePrice !== guestSalePrice ||
            !isSamePriceRange(memberPriceRange, guestPriceRangeData));

        // Helper to check if guest price is greater than current group's price (so we show guest as original/crossed out).
        // Compares effective (sale-inclusive) prices, not just base price — a group can be
        // genuinely discounted via `salePrice` alone while its base `price` is unchanged.
        const isGuestPriceGreaterThanCurrentGroup = (() => {
          const guestEffectivePrice =
            guestSalePrice > 0 ? guestSalePrice : guestPrice;
          const selectedEffectivePrice =
            selectedSalePrice > 0 ? selectedSalePrice : selectedPrice;
          // For simple prices, compare numeric values
          if (guestEffectivePrice != null && selectedEffectivePrice != null) {
            return guestEffectivePrice > selectedEffectivePrice;
          }
          // For price ranges, compare low prices
          const guestEffectiveRangeLow =
            guestPriceRangeData?.salePrice?.[0] ??
            guestPriceRangeData?.price?.[0];
          const selectedEffectiveRangeLow =
            selectedPriceRange?.salePrice?.[0] ??
            selectedPriceRange?.price?.[0];
          if (
            guestEffectiveRangeLow != null &&
            selectedEffectiveRangeLow != null
          ) {
            return guestEffectiveRangeLow > selectedEffectiveRangeLow;
          }
          return false;
        })();

        // Show guest price as original (crossed out) when viewing any non-guest group AND guest > current group price
        const shouldShowGuestPriceComparison =
          hasMounted &&
          priceGroupKey !== 'guest' &&
          isGuestPriceGreaterThanCurrentGroup;

        // Current group price differs from guest — apply green price on PLP (no label)
        const hasCustomerGroupPriceDifference =
          hasMounted &&
          priceGroupKey !== 'guest' &&
          (selectedPrice !== guestPrice ||
            selectedSalePrice !== guestSalePrice ||
            !isSamePriceRange(selectedPriceRange, guestPriceRangeData));

        return {
          ...dict,
          [variant]: {
            swatchName: variant,
            swatch,
            image,
            imageTransparent,
            badge,
            badgeGuest,
            sku,
            price: selectedPrice,
            salePrice: selectedSalePrice,
            priceFormatted,
            salePriceFormatted,
            priceRange: {
              price: normalPriceRange,
              salePrice: salePriceRange,
            },
            guestPriceFormatted,
            guestSalePriceFormatted,
            guestPriceRange: {
              price: guestNormalPriceRange,
              salePrice: guestSalePriceRange,
            },
            hasMemberPriceDifference,
            shouldShowGuestPriceComparison,
            hasCustomerGroupPriceDifference,
            colourQuerySlug: new URLSearchParams({
              colour: variant,
            }).toString(),
            isActive: activeVariant === variant,
            prices,
            onClick: () => {
              if (algoliaDebug) {
                console.log('swatch clicked', {
                  colourQuerySlug: new URLSearchParams({
                    colour: variant,
                  }).toString(),
                  variant,
                  swatch,
                  image,
                  imageTransparent,
                  badge,
                  price: selectedPrice,
                  salePrice: selectedSalePrice,
                });
              }
              setActiveVariantCallback(variant);
            },
          },
        };
      },
      {}
    );

    if (Object.values(result).length === 0) {
      const { priceFormatted, salePriceFormatted } = buildPrice(
        defaultState.price,
        defaultState.salePrice,
        undefined,
        locale
      );
      result = {
        [hitColour]: {
          ...defaultState,
          swatch: hitColour,
          priceFormatted,
          salePriceFormatted,
          guestPriceFormatted: priceFormatted,
          guestSalePriceFormatted: salePriceFormatted,
          priceRange: undefined,
          guestPriceRange: undefined,
          hasMemberPriceDifference: false,
          shouldShowGuestPriceComparison: false,
          hasCustomerGroupPriceDifference: false,
          colourQuerySlug: new URLSearchParams({
            colour: hitColour,
          }).toString(),
          swatchName: hitColour,
          isActive: true,
          onClick: () => {
            if (algoliaDebug) {
              console.log('swatch clicked (fallback)', {
                colourQuerySlug: new URLSearchParams({
                  colour: hitColour,
                }).toString(),
                variant: hitColour,
                image: defaultState.image,
                imageTransparent: defaultState.imageTransparent,
                badge: defaultState.badge,
                price: defaultState.price,
                salePrice: defaultState.salePrice,
              });
            }
            setActiveVariantCallback(hitColour);
          },
        },
      };
    }

    return result;
  }, [
    variants,
    hitColour,
    defaultState,
    locale,
    priceGroupKey,
    hasMounted,
    activeVariant,
    algoliaDebug,
    setActiveVariantCallback,
  ]);

  return {
    activeVariant: swatchesRecord[activeVariant],
    swatches: Object.values(swatchesRecord),
    prices: swatchesRecord[activeVariant]?.prices ?? {},
    priceGroupKey,
  };
};
